import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';
import { useLocation } from 'react-router-dom';
import { Sidebar } from '@/widgets/Sidebar';
import { Header } from '@/widgets/Header';
import { UploadsTray } from '@/widgets/UploadsTray';
import { useNotificationsRealtime } from '@/features/Chat';
import { FloatingContext } from '@/shared/lib/components/FloatingProvider';
import { AdminAccessProvider } from '@/shared/lib/adminAccess';

export type PageMeta = {
  title?: string;
  description?: string;
};

interface MainLayoutProps {
  children: ReactNode;
  meta?: PageMeta;
  headerTitle?: string;
  headerDescription?: string;
  headerInputPlaceholder?: string;
  headerButtonText?: string;
  headerOnPress?: () => void;
  headerGoBackButton?: boolean;
  onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchValue?: string;
  actionsDisabled?: boolean;
  onUserActions?: () => void;
  onSettings?: () => void;
  onLogout?: () => void;
  classNameWrapMain?: string;
  classNameMain?: string;
}

export const MainLayout = ({
  children,
  meta,
  onUserActions,
  onSettings,
  onLogout,
  classNameWrapMain,
  classNameMain,
}: MainLayoutProps) => {
  const { pathname } = useLocation();
  const { setScrollLockTarget } = React.useContext(FloatingContext);
  const contentScrollRef = useRef<HTMLDivElement | null>(null);

  // Тост об уведомлении должен всплывать на любом экране, поэтому подписка живёт здесь:
  // макет один на приложение, а экран списка уведомлений — нет.
  useNotificationsRealtime();

  const scrollRef = useCallback(
    (el: HTMLDivElement | null) => {
      contentScrollRef.current = el;
      setScrollLockTarget(el);
    },
    [setScrollLockTarget],
  );

  useEffect(() => {
    contentScrollRef.current?.scrollTo({ top: 0, left: 0 });
    window.scrollTo({ top: 0, left: 0 });
  }, [pathname]);

  useEffect(() => {
    document.title = meta?.title || 'Ihcl Admin';
    if (meta?.description) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', meta.description);
      } else {
        const metaTag = document.createElement('meta');
        metaTag.name = 'description';
        metaTag.content = meta.description;
        document.head.appendChild(metaTag);
      }
    }
  }, [meta]);

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <AdminAccessProvider>
        <div className="flex min-h-0 flex-1 flex-col gap-6 py-6">
          <Header onUserActions={onUserActions} onSettings={onSettings} onLogout={onLogout} />
          <div className="relative flex min-h-0 w-full flex-1 gap-6">
            <Sidebar />
            <div
              ref={scrollRef}
              className={cn(
                'flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-x-hidden overflow-y-auto rounded-[24px]',
                classNameWrapMain,
              )}
            >
              <main
                className={cn(
                  'app-shell-main flex min-h-full min-w-0 w-full flex-1 flex-col bg-white p-6',
                  classNameMain,
                )}
              >
                {children}
              </main>
            </div>
          </div>
        </div>
      </AdminAccessProvider>
      <UploadsTray />
    </div>
  );
};
